import React from "react";
import { EnrichedLoyaltyTier } from "../../contexts/CustomerContext";
import { formatLoyaltyAmount } from "../../utils/format";
import styles from "./LoyaltyModal.module.scss";

const TierRow: React.FC<{
  tier: EnrichedLoyaltyTier;
  isCurrent: boolean;
  isLast: boolean;
  spend: number;
  nextThreshold: number | null;
  pointsRate: number | null;
}> = ({ tier, isCurrent, isLast, spend, nextThreshold }) => {
  const color = tier.color ?? "#9ca3af";
  const reached = spend >= tier.threshold;
  const multiplier = tier.multiplier ?? 1;

  const progressPct =
    isCurrent && nextThreshold != null
      ? Math.min(
          100,
          Math.round(
            ((spend - tier.threshold) / (nextThreshold - tier.threshold)) * 100,
          ),
        )
      : reached
        ? 100
        : 0;

  const unlockLabel =
    tier.threshold === 0
      ? "Starting tier"
      : reached
        ? `Unlocked at ${formatLoyaltyAmount(tier.threshold)} spend`
        : `${formatLoyaltyAmount(tier.threshold - spend)} more to unlock`;

  return (
    <div className={styles.TierRow}>
      <div className={styles.TierTrack}>
        <div
          className={`${styles.TierDot} ${reached ? styles.TierDotReached : styles.TierDotLocked}`}
          style={
            reached
              ? { backgroundColor: color, boxShadow: `0 0 0 4px ${color}28` }
              : {}
          }
        >
          {reached ? <span>{isCurrent ? "★" : "✓"}</span> : <span>○</span>}
        </div>
        {!isLast && (
          <div className={styles.TierLine}>
            <div
              className={styles.TierLineFill}
              style={{ height: `${progressPct}%`, backgroundColor: color }}
            />
          </div>
        )}
      </div>

      <div
        className={`${styles.TierInfo} ${isCurrent ? styles.TierInfoCurrent : ""}`}
        style={
          isCurrent
            ? { borderColor: `${color}44`, backgroundColor: `${color}0d` }
            : {}
        }
      >
        <div className={styles.TierInfoTop}>
          <div className={styles.TierInfoLeft}>
            {isCurrent && (
              <span
                className={styles.CurrentBadge}
                style={{ backgroundColor: color }}
              >
                CURRENT
              </span>
            )}
            <span
              className={`${styles.TierName} ${!reached ? styles.TierNameLocked : ""}`}
            >
              {tier.name}
            </span>
          </div>
          <div className={styles.TierBadges}>
            <span
              className={styles.MultiplierBadge}
              style={
                reached
                  ? {
                      color,
                      backgroundColor: `${color}12`,
                      borderColor: `${color}30`,
                    }
                  : {}
              }
            >
              {multiplier}× pts
            </span>
            {tier.discountPercent != null && tier.discountPercent > 0 && (
              <span
                className={styles.DiscountBadge}
                style={
                  reached
                    ? {
                        color,
                        backgroundColor: `${color}12`,
                        borderColor: `${color}30`,
                      }
                    : {}
                }
              >
                {tier.discountPercent}% off
              </span>
            )}
          </div>
        </div>

        <div className={styles.TierInfoFooter}>
          <p className={styles.TierSubtext}>{unlockLabel}</p>
        </div>
      </div>
    </div>
  );
};

export default TierRow;
